import { Phone, Mail, MapPin } from 'lucide-react';
import { useNavigate, useLocation } from 'react-router-dom';

export default function Footer() {
  const navigate = useNavigate();
  const location = useLocation();
  const isHomePage = location.pathname === '/';

  const scrollToSection = (id: string) => {
    if (!isHomePage) {
      navigate('/');
      setTimeout(() => {
        const element = document.getElementById(id);
        if (element) {
          element.scrollIntoView({ behavior: 'smooth' });
        }
      }, 100);
    } else {
      const element = document.getElementById(id);
      if (element) {
        element.scrollIntoView({ behavior: 'smooth' });
      }
    }
  };

  const addresses = {
    bern: {
      street: 'Mühledorfstrasse 1',
      city: '3018 Bern',
    },
    aargau: {
      street: 'Hintere Bahnhofstrasse 83',
      city: '5000 Aarau',
    },
    luzern: {
      street: 'Grossmatte 7',
      city: '6014 Luzern',
    },
    zug: {
      street: 'Baarerstrasse 77',
      city: '6300 Zug',
    },
    zurich: {
      street: 'Parkstrasse 4',
      city: '8304 Wallisellen',
    },
  };

  const getAddressForPage = () => {
    const path = location.pathname.toLowerCase();

    if (path.includes('bern')) {
      return addresses.bern;
    } else if (path.includes('aargau')) {
      return addresses.aargau;
    } else if (path.includes('luzern')) {
      return addresses.luzern;
    } else if (path.includes('zuerich') || path.includes('zurich') || path.includes('winterthur')) {
      return addresses.zurich;
    } else if (path.includes('stgallen') || path.includes('st-gallen')) {
      return addresses.zurich;
    } else if (path.includes('-zug-')) {
      return addresses.zug;
    } else if (path.includes('basel')) {
      return addresses.aargau;
    }

    return addresses.aargau;
  };

  const currentAddress = getAddressForPage();

  const cities = [
    { name: 'Umzug Zürich Deutschland', link: '/umzug-zuerich-deutschland' },
    { name: 'Umzug Basel Deutschland', link: '/umzug-basel-deutschland' },
    { name: 'Umzug Bern Deutschland', link: '/umzug-bern-deutschland' },
    { name: 'Umzug Luzern Deutschland', link: '/umzug-luzern-deutschland' },
    { name: 'Umzug Aargau Deutschland', link: '/umzug-aargau-deutschland' },
    { name: 'Umzug Winterthur Deutschland', link: '/umzug-winterthur-deutschland' },
    { name: 'Umzug St. Gallen Deutschland', link: '/umzug-stgallen-deutschland' },
    { name: 'Umzug Zug Deutschland', link: '/umzug-zug-deutschland' },
  ];

  return (
    <footer className="bg-slate-900 text-white pt-16 pb-8">
      <div className="container mx-auto px-4">
        <div className="grid md:grid-cols-2 lg:grid-cols-5 gap-8 mb-12">
          <div>
            <div className="flex items-center space-x-2 mb-4">
              <div className="bg-white text-slate-900 w-10 h-10 rounded-lg flex items-center justify-center font-bold text-lg">
                U
              </div>
              <div className="font-bold text-lg">
                umzug-schweiz-deutschland.ch
              </div>
            </div>
            <p className="text-slate-400 mb-6 leading-relaxed">
              Ihr zuverlässiger Partner für professionelle Umzüge von der Schweiz nach Deutschland. Stressfrei, schnell und mit Rundum-Service.
            </p>
            <div className="space-y-3">
              <div className="flex items-start space-x-2 text-slate-300">
                <MapPin className="w-4 h-4 mt-1 flex-shrink-0" />
                <div>
                  <div className="font-semibold text-white">Clean & Umzug Profis AG</div>
                  <div className="text-sm">{currentAddress.street}</div>
                  <div className="text-sm">{currentAddress.city}</div>
                </div>
              </div>
              <a href="tel:0800700100" className="flex items-center space-x-2 text-slate-300 hover:text-white transition">
                <Phone className="w-4 h-4" />
                <div>
                  <div>0800 700 100</div>
                  <div className="text-xs text-green-400">Gratis Hotline</div>
                </div>
              </a>
              <a href="mailto:info@umzugprofis.ch" className="flex items-center space-x-2 text-slate-300 hover:text-white transition">
                <Mail className="w-4 h-4" />
                <span>info@umzugprofis.ch</span>
              </a>
            </div>
          </div>

          <div>
            <h3 className="font-bold text-lg mb-4">Navigation</h3>
            <ul className="space-y-2">
              <li>
                <a
                  href="/leistungen"
                  className="text-slate-400 hover:text-white transition"
                  onClick={(e) => {
                    e.preventDefault();
                    navigate('/leistungen');
                  }}
                >
                  Leistungen
                </a>
              </li>
              <li>
                <a
                  href="/preise"
                  className="text-slate-400 hover:text-white transition"
                  onClick={(e) => {
                    e.preventDefault();
                    navigate('/preise');
                  }}
                >
                  Preise
                </a>
              </li>
              <li>
                <a
                  href="/ablauf"
                  className="text-slate-400 hover:text-white transition"
                  onClick={(e) => {
                    e.preventDefault();
                    navigate('/ablauf');
                  }}
                >
                  Ablauf
                </a>
              </li>
              <li>
                <a
                  href="/faq"
                  className="text-slate-400 hover:text-white transition"
                  onClick={(e) => {
                    e.preventDefault();
                    navigate('/faq');
                  }}
                >
                  FAQ
                </a>
              </li>
              <li>
                <a
                  href="/kontakt"
                  className="text-slate-400 hover:text-white transition"
                  onClick={(e) => {
                    e.preventDefault();
                    navigate('/kontakt');
                  }}
                >
                  Kontakt
                </a>
              </li>
            </ul>
          </div>

          <div>
            <h3 className="font-bold text-lg mb-4">Unsere Services</h3>
            <ul className="space-y-2 text-slate-400 text-sm">
              <li>
                <a
                  href="/komplettumzug"
                  className="hover:text-white transition"
                  onClick={(e) => {
                    e.preventDefault();
                    navigate('/komplettumzug');
                  }}
                >
                  Komplettumzug
                </a>
              </li>
              <li>
                <a
                  href="/teilumzug"
                  className="hover:text-white transition"
                  onClick={(e) => {
                    e.preventDefault();
                    navigate('/teilumzug');
                  }}
                >
                  Teilumzug
                </a>
              </li>
              <li>
                <a
                  href="/zollabwicklung"
                  className="hover:text-white transition"
                  onClick={(e) => {
                    e.preventDefault();
                    navigate('/zollabwicklung');
                  }}
                >
                  Zollabwicklung
                </a>
              </li>
              <li>
                <a
                  href="/verpackungsmaterial"
                  className="hover:text-white transition"
                  onClick={(e) => {
                    e.preventDefault();
                    navigate('/verpackungsmaterial');
                  }}
                >
                  Verpackungsmaterial
                </a>
              </li>
              <li>
                <a
                  href="/zwischenlagerung"
                  className="hover:text-white transition"
                  onClick={(e) => {
                    e.preventDefault();
                    navigate('/zwischenlagerung');
                  }}
                >
                  Zwischenlagerung
                </a>
              </li>
            </ul>
          </div>

          <div>
            <h3 className="font-bold text-lg mb-4">Weitere Services</h3>
            <ul className="space-y-2 text-slate-400 text-sm">
              <li>
                <a
                  href="/endreinigung"
                  className="hover:text-white transition"
                  onClick={(e) => {
                    e.preventDefault();
                    navigate('/endreinigung');
                  }}
                >
                  Endreinigung
                </a>
              </li>
              <li>
                <a
                  href="/firmenumzuege"
                  className="hover:text-white transition"
                  onClick={(e) => {
                    e.preventDefault();
                    navigate('/firmenumzuege');
                  }}
                >
                  Firmenumzüge
                </a>
              </li>
              <li>
                <a
                  href="/express-umzuege"
                  className="hover:text-white transition"
                  onClick={(e) => {
                    e.preventDefault();
                    navigate('/express-umzuege');
                  }}
                >
                  Express-Umzüge
                </a>
              </li>
              <li>
                <a
                  href="/sofort-besichtigung"
                  className="hover:text-white transition"
                  onClick={(e) => {
                    e.preventDefault();
                    navigate('/sofort-besichtigung');
                  }}
                >
                  Sofort Besichtigung
                </a>
              </li>
            </ul>
          </div>

          <div>
            <h3 className="font-bold text-lg mb-4">Regionen</h3>
            <ul className="space-y-2 text-slate-400 text-sm">
              {cities.slice(0, 8).map((city, index) => (
                <li key={index}>
                  {city.link ? (
                    <a
                      href={city.link}
                      className="hover:text-white transition"
                      onClick={(e) => {
                        e.preventDefault();
                        navigate(city.link!);
                      }}
                    >
                      {city.name}
                    </a>
                  ) : (
                    city.name
                  )}
                </li>
              ))}
            </ul>
          </div>
        </div>

        <div className="border-t border-slate-800 pt-8">
          <div className="flex flex-col md:flex-row justify-between items-center space-y-4 md:space-y-0">
            <div className="text-slate-400 text-sm">
              © {new Date().getFullYear()} umzug-schweiz-deutschland.ch. Alle Rechte vorbehalten.
            </div>
            <div className="flex flex-wrap justify-center gap-4 md:gap-6 text-sm">
              <a
                href="/sitemap"
                className="text-slate-400 hover:text-white transition"
                onClick={(e) => {
                  e.preventDefault();
                  navigate('/sitemap');
                }}
              >
                Sitemap
              </a>
              <a
                href="/datenschutz"
                className="text-slate-400 hover:text-white transition"
                onClick={(e) => {
                  e.preventDefault();
                  navigate('/datenschutz');
                }}
              >
                Datenschutz
              </a>
              <a
                href="/agb"
                className="text-slate-400 hover:text-white transition"
                onClick={(e) => {
                  e.preventDefault();
                  navigate('/agb');
                }}
              >
                AGB
              </a>
              <a
                href="/impressum"
                className="text-slate-400 hover:text-white transition"
                onClick={(e) => {
                  e.preventDefault();
                  navigate('/impressum');
                }}
              >
                Impressum
              </a>
            </div>
          </div>

          <div className="mt-6 text-center">
            <a
              href="https://www.digitaldominanz.ch"
              target="_blank"
              rel="noopener noreferrer"
              className="group inline-flex items-center space-x-2 text-slate-500 hover:text-white transition-all duration-300 text-sm"
            >
              <span className="relative">
                <span className="relative z-10">Webdesign & Marketing by</span>
                <span className="absolute inset-0 bg-gradient-to-r from-blue-500/0 via-blue-500/20 to-blue-500/0 blur-sm opacity-0 group-hover:opacity-100 transition-opacity duration-300"></span>
              </span>
              <span className="font-semibold bg-gradient-to-r from-blue-400 to-cyan-400 bg-clip-text text-transparent group-hover:from-blue-300 group-hover:to-cyan-300 transition-all duration-300">
                digitaldominanz.ch
              </span>
              <svg
                className="w-4 h-4 text-slate-500 group-hover:text-blue-400 group-hover:translate-x-1 transition-all duration-300"
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
              >
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10 6H6a2 2 0 00-2 2v10a2 2 0 002 2h10a2 2 0 002-2v-4M14 4h6m0 0v6m0-6L10 14" />
              </svg>
            </a>
          </div>
        </div>
      </div>
    </footer>
  );
}
